/* src/pages/MyProfile.jsx
 * User profile page - personal settings, password change, workspace switching
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { getUserWorkspaceAccess } from '../utils/accessControl';
import { usageApi } from '../utils/api';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useIsMobile } from '../hooks/useIsMobile';
import LogoUrlUploadField from '../components/LogoUrlUploadField';
import CreateClientWorkspaceControl from '../components/CreateClientWorkspaceControl';

export default function MyProfile() {
  const { currentUser, userProfile, loadUserProfile } = useAuth();
  const {
    selectedWorkspaceId,
    workspaceDetails: contextDetails,
    switchWorkspace,
    reloadWorkspaces,
    archiveWorkspace,
    unarchiveWorkspace,
  } = useWorkspace();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [workspaceDetails, setWorkspaceDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  
  // Create workspace state
  const [workspaceError, setWorkspaceError] = useState(null);
  const [workspaceSuccess, setWorkspaceSuccess] = useState(null);
  
  // Workspace name editing state
  const [editingWorkspaceName, setEditingWorkspaceName] = useState(null);
  const [workspaceNameValue, setWorkspaceNameValue] = useState('');
  const [savingWorkspaceName, setSavingWorkspaceName] = useState(false);
  const [savingDefaultLogoId, setSavingDefaultLogoId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [confirmArchiveId, setConfirmArchiveId] = useState(null);
  const [archivePending, setArchivePending] = useState(false);
  const [immediateEnabled, setImmediateEnabled] = useState(
    userProfile?.notificationPrefs?.immediateEnabled !== false
  );
  const [digestEnabled, setDigestEnabled] = useState(
    userProfile?.notificationPrefs?.digestEnabled !== false
  );
  const [digestHour, setDigestHour] = useState(userProfile?.notificationPrefs?.digestHour ?? 8);
  const [savingNotifyPrefs, setSavingNotifyPrefs] = useState(false);
  const [notifyPrefsMessage, setNotifyPrefsMessage] = useState(null);

  useEffect(() => {
    if (currentUser && contextDetails.length > 0) {
      loadWorkspaceDetails();
    }
  }, [currentUser, contextDetails, selectedWorkspaceId]);

  useEffect(() => {
    if (!selectedWorkspaceId) return undefined;
    let cancelled = false;
    usageApi.getUsage(selectedWorkspaceId)
      .then((data) => {
        if (!cancelled && data?.success) setUsage(data);
      })
      .catch((error) => {
        console.error('Error loading usage:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (!userProfile) return;
    setImmediateEnabled(userProfile.notificationPrefs?.immediateEnabled !== false);
    setDigestEnabled(userProfile.notificationPrefs?.digestEnabled !== false);
    setDigestHour(userProfile.notificationPrefs?.digestHour ?? 8);
  }, [userProfile]);

  async function handleSaveNotifyPrefs() {
    if (!currentUser) return;
    try {
      setSavingNotifyPrefs(true);
      setNotifyPrefsMessage(null);
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'notificationPrefs.immediateEnabled': immediateEnabled,
        'notificationPrefs.digestEnabled': digestEnabled,
        'notificationPrefs.digestHour': Number(digestHour),
        'notificationPrefs.timezone':
          userProfile?.notificationPrefs?.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'America/New_York',
      });
      setNotifyPrefsMessage('Notification preferences saved.');
      if (loadUserProfile) await loadUserProfile(currentUser.uid);
    } catch (error) {
      console.error('Error saving notification prefs:', error);
      setNotifyPrefsMessage('Failed to save notification preferences.');
    } finally {
      setSavingNotifyPrefs(false);
    }
  }

  async function loadWorkspaceDetails() {
    if (!currentUser) return;

    try {
      setLoading(true);
      const details = [];

      for (const entry of contextDetails) {
        const workspaceId = entry.id;
        const isOwner = entry.role === 'owner';
        const access = isOwner
          ? { leadAccess: true, aiReplyAccess: true }
          : await getUserWorkspaceAccess(currentUser.uid, workspaceId);

        let workspaceName = entry.name
          || (workspaceId === currentUser.uid ? 'My Workspace' : 'Team Workspace');
        let defaultLogoUrl = '';
        let ownerEmail = null;
        try {
          const workspaceDoc = await getDoc(doc(db, 'workspaces', workspaceId));
          if (workspaceDoc.exists()) {
            const workspaceData = workspaceDoc.data();
            if (workspaceData.name) {
              workspaceName = workspaceData.name;
            } else if (!isOwner && workspaceData.ownerId) {
              const ownerDoc = await getDoc(doc(db, 'users', workspaceData.ownerId));
              if (ownerDoc.exists()) {
                const email = ownerDoc.data().email;
                workspaceName = email ? `${email}'s Workspace` : workspaceName;
                ownerEmail = email || null;
              }
            }
            defaultLogoUrl = workspaceData.defaultLogoUrl || '';
            if (!isOwner && !ownerEmail && workspaceData.ownerId) {
              const ownerDoc = await getDoc(doc(db, 'users', workspaceData.ownerId));
              if (ownerDoc.exists()) {
                ownerEmail = ownerDoc.data().email || null;
              }
            }
          }
        } catch (error) {
          console.error('Error loading workspace details:', error);
        }

        details.push({
          workspaceId,
          name: workspaceName,
          defaultLogoUrl,
          isOwner,
          ownerEmail,
          leadAccess: access.leadAccess,
          aiReplyAccess: access.aiReplyAccess,
          isSelected: workspaceId === selectedWorkspaceId,
          archivedAt: entry.archivedAt,
        });
      }

      setWorkspaceDetails(details);
    } catch (error) {
      console.error('Error loading workspace details:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    
    if (!currentUser || !currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      setChangingPassword(true);
      setPasswordError(null);
      setPasswordSuccess(null);

      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('New password is too weak');
      } else {
        setPasswordError(error.message || 'Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  }

  function handleSwitchWorkspace(workspaceId) {
    switchWorkspace(workspaceId);
    loadWorkspaceDetails();
    navigate('/dashboard');
  }

  async function handleArchive(workspaceId) {
    try {
      setArchivePending(true);
      setWorkspaceError(null);
      await archiveWorkspace(workspaceId);
      setConfirmArchiveId(null);
      setWorkspaceSuccess('Workspace archived.');
    } catch (error) {
      console.error('Error archiving workspace:', error);
      setWorkspaceError(error.message || 'Failed to archive workspace.');
    } finally {
      setArchivePending(false);
    }
  }

  async function handleRestore(workspaceId) {
    try {
      setArchivePending(true);
      setWorkspaceError(null);
      await unarchiveWorkspace(workspaceId);
      setWorkspaceSuccess('Workspace restored.');
    } catch (error) {
      console.error('Error restoring workspace:', error);
      setWorkspaceError(error.message || 'Failed to restore workspace.');
    } finally {
      setArchivePending(false);
    }
  }

  function handleStartEditWorkspaceName(workspace) {
    if (!workspace.isOwner) return; // Only owners can edit
    setEditingWorkspaceName(workspace.workspaceId);
    setWorkspaceNameValue(workspace.name);
  }

  async function handleSaveWorkspaceName(workspaceId) {
    if (!currentUser || !workspaceNameValue.trim()) return;

    try {
      setSavingWorkspaceName(true);
      setWorkspaceError(null);
      setWorkspaceSuccess(null);

      // Update workspace name
      await updateDoc(doc(db, 'workspaces', workspaceId), {
        name: workspaceNameValue.trim(),
      });

      setWorkspaceSuccess('Workspace name updated successfully!');
      setEditingWorkspaceName(null);
      setWorkspaceNameValue('');

      await reloadWorkspaces();
      loadWorkspaceDetails();
    } catch (error) {
      console.error('Error updating workspace name:', error);
      setWorkspaceError('Failed to update workspace name. Please try again.');
    } finally {
      setSavingWorkspaceName(false);
    }
  }

  function handleCancelEditWorkspaceName() {
    setEditingWorkspaceName(null);
    setWorkspaceNameValue('');
  }

  async function handleSaveDefaultLogo(workspaceId, url) {
    if (!currentUser) return;

    try {
      setSavingDefaultLogoId(workspaceId);
      setWorkspaceError(null);
      setWorkspaceSuccess(null);

      await updateDoc(doc(db, 'workspaces', workspaceId), {
        defaultLogoUrl: url?.trim() || null,
      });

      setWorkspaceDetails((prev) =>
        prev.map((workspace) =>
          workspace.workspaceId === workspaceId
            ? { ...workspace, defaultLogoUrl: url?.trim() || '' }
            : workspace
        )
      );
      setWorkspaceSuccess('Default email logo updated.');
    } catch (error) {
      console.error('Error updating default logo:', error);
      setWorkspaceError('Failed to update default email logo. Please try again.');
    } finally {
      setSavingDefaultLogoId(null);
    }
  }

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';

  if (loading) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page profile-page" style={{ padding: pagePad }}>
          <h1 className="today-h1">My Profile</h1>
          <p style={{ color: 'var(--muted)', margin: 0 }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page profile-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left">
            <h1 className="today-h1">My Profile</h1>
          </div>
        </div>

        <div className="today-panel profile-section-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Personal Information</span>
          </div>
          <div className="today-panel-body">
            <div className="profile-field">
              <label className="profile-field-label">Email Address</label>
              <input type="email" className="fb-input" value={currentUser?.email || ''} disabled />
              <p className="profile-field-hint">
                Email address cannot be changed. Contact support if you need to change it.
              </p>
            </div>

            <div className="profile-field">
              <label className="profile-field-label">User ID</label>
              <input
                type="text"
                className="fb-input"
                value={currentUser?.uid || ''}
                disabled
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.9rem' }}
              />
            </div>

            {userProfile && (
              <div className="profile-field" style={{ marginBottom: 0 }}>
                <label className="profile-field-label">Account Tier</label>
                <input
                  type="text"
                  className="fb-input"
                  value={userProfile.tier || 'free'}
                  disabled
                  style={{ textTransform: 'capitalize' }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="today-panel profile-section-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Notifications</span>
          </div>
          <div className="today-panel-body">
            <p className="profile-field-hint" style={{ marginTop: 0 }}>
              Immediate emails fire only for a new lead or a lead that comes back after more than an hour of silence.
              Everything else lands in one daily digest covering all of your workspaces.
            </p>
            <label className="profile-field profile-notify-row" style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={immediateEnabled}
                onChange={(e) => setImmediateEnabled(e.target.checked)}
              />
              Immediate emails for new and returning leads
            </label>
            <label className="profile-field profile-notify-row" style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={digestEnabled}
                onChange={(e) => setDigestEnabled(e.target.checked)}
              />
              Daily digest
            </label>
            <div className="profile-field">
              <label className="profile-field-label">Digest time</label>
              <select
                className="fb-input"
                value={digestHour}
                onChange={(e) => setDigestHour(Number(e.target.value))}
                disabled={!digestEnabled}
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>
                    {hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`}
                  </option>
                ))}
              </select>
              <p className="profile-field-hint">Uses your browser timezone. Default is 8:00 AM.</p>
            </div>
            <button
              type="button"
              className="inbox-pill-btn-primary"
              onClick={handleSaveNotifyPrefs}
              disabled={savingNotifyPrefs}
            >
              {savingNotifyPrefs ? 'Saving…' : 'Save notification preferences'}
            </button>
            {notifyPrefsMessage && (
              <p className="profile-field-hint" style={{ marginBottom: 0 }}>{notifyPrefsMessage}</p>
            )}
          </div>
        </div>

        <div className="today-panel profile-section-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Change Password</span>
          </div>
          <div className="today-panel-body">
            <form onSubmit={handlePasswordChange}>
              <div className="profile-field">
                <label className="profile-field-label">Current Password</label>
                <input
                  type="password"
                  className="fb-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>

              <div className="profile-field">
                <label className="profile-field-label">New Password</label>
                <input
                  type="password"
                  className="fb-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>

              <div className="profile-field">
                <label className="profile-field-label">Confirm New Password</label>
                <input
                  type="password"
                  className="fb-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>

              {passwordError && (
                <div className="today-load-error" style={{ marginBottom: '1rem' }}>{passwordError}</div>
              )}

              {passwordSuccess && (
                <div className="fb-callout success" style={{ marginBottom: '1rem' }}>{passwordSuccess}</div>
              )}

              <button
                type="submit"
                className="inbox-pill-btn-primary"
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              >
                {changingPassword ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>

        {workspaceDetails.length > 0 && (
          <div className="today-panel profile-section-panel">
            <div className="today-panel-header profile-workspace-header">
              <div>
                <span className="today-panel-title">Workspaces</span>
                <p style={{ color: 'var(--muted)', margin: '0.5rem 0 0', fontSize: '0.9rem', lineHeight: 1.5 }}>
                  Each client gets its own workspace - forms, inbox, pipeline, operator. Your plan pools leads across all of them.
                </p>
              </div>
              <button
                type="button"
                className="inbox-pill-btn"
                onClick={() => {
                  reloadWorkspaces();
                  setTimeout(() => {
                    loadWorkspaceDetails();
                  }, 500);
                }}
                title="Refresh workspaces"
                style={{ flexShrink: 0 }}
              >
                Refresh
              </button>
            </div>
            <div className="today-panel-body">
              {(workspaceError || workspaceSuccess) && (
                <p style={{
                  color: workspaceError ? 'var(--danger, #b91c1c)' : 'var(--muted)',
                  fontSize: '0.9rem',
                  margin: '0 0 0.75rem',
                }}>
                  {workspaceError || workspaceSuccess}
                </p>
              )}
              <div className="profile-workspace-list">
                {workspaceDetails.filter((w) => !w.archivedAt).map(renderWorkspaceCard)}
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <CreateClientWorkspaceControl usage={usage} />
              </div>
              {workspaceDetails.some((w) => w.archivedAt) && (
                <div style={{ marginTop: '1.25rem' }}>
                  <button
                    type="button"
                    className="inbox-pill-btn"
                    onClick={() => setArchivedOpen((open) => !open)}
                  >
                    {archivedOpen ? 'Hide archived' : 'Archived'}
                  </button>
                  {archivedOpen && (
                    <div className="profile-workspace-list" style={{ marginTop: '0.75rem' }}>
                      {workspaceDetails.filter((w) => w.archivedAt).map(renderWorkspaceCard)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function renderWorkspaceCard(workspace) {
    const isUidWorkspace = workspace.workspaceId === currentUser?.uid;
    const archived = !!workspace.archivedAt;
    return (
      <div
        key={workspace.workspaceId}
        className={`profile-workspace-card${workspace.isSelected ? ' profile-workspace-card--selected' : ''}${editingWorkspaceName === workspace.workspaceId ? ' profile-workspace-card--editing' : ''}`}
        onClick={(e) => {
          if (archived || workspace.isSelected) return;
          if (editingWorkspaceName !== workspace.workspaceId) {
            handleSwitchWorkspace(workspace.workspaceId);
          }
        }}
      >
        <div className="profile-workspace-card-inner">
          <div style={{ flex: 1 }}>
            <div className="profile-workspace-title-row">
              {editingWorkspaceName === workspace.workspaceId ? (
                <div className="profile-workspace-edit-row">
                  <input
                    type="text"
                    className="fb-input profile-workspace-edit-input"
                    value={workspaceNameValue}
                    onChange={(e) => setWorkspaceNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        handleSaveWorkspaceName(workspace.workspaceId);
                      } else if (e.key === 'Escape') {
                        e.stopPropagation();
                        handleCancelEditWorkspaceName();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Workspace name"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="inbox-pill-btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveWorkspaceName(workspace.workspaceId);
                    }}
                    disabled={savingWorkspaceName || !workspaceNameValue.trim()}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="inbox-pill-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelEditWorkspaceName();
                    }}
                    disabled={savingWorkspaceName}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <span className="profile-workspace-name">{workspace.name}</span>
                  {workspace.isOwner && !archived && (
                    <button
                      type="button"
                      className="inbox-pill-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEditWorkspaceName(workspace);
                      }}
                      title="Edit workspace name"
                      style={{ padding: '0.25rem 0.65rem', fontSize: '0.85rem' }}
                    >
                      Edit Name
                    </button>
                  )}
                  {workspace.isSelected && (
                    <span className="inbox-chip accent-soft">Active</span>
                  )}
                  {workspace.isOwner && (
                    <span className="inbox-chip">Owner</span>
                  )}
                </>
              )}
            </div>
            {!workspace.isOwner && workspace.ownerEmail && (
              <p className="profile-workspace-meta">Owner: {workspace.ownerEmail}</p>
            )}
            <div className="profile-workspace-badges">
              {workspace.leadAccess && (
                <span className="inbox-chip accent-soft">Lead Access</span>
              )}
              {workspace.aiReplyAccess && (
                <span className="inbox-chip accent">AI Reply Access</span>
              )}
            </div>
            {workspace.isOwner && !archived && (
              <div
                style={{ marginTop: '1rem' }}
                onClick={(e) => e.stopPropagation()}
              >
                <label className="fb-label" style={{ display: 'block', marginBottom: '0.35rem' }}>
                  Default email logo
                </label>
                <LogoUrlUploadField
                  value={workspace.defaultLogoUrl || ''}
                  onChange={(url) => handleSaveDefaultLogo(workspace.workspaceId, url)}
                  uploadType="workspace"
                  uploadIdValue={workspace.workspaceId}
                  disabled={savingDefaultLogoId === workspace.workspaceId}
                  helperText="Used on emails for forms that do not set their own logo. Upload or paste a public image URL."
                />
              </div>
            )}
            {workspace.isOwner && !isUidWorkspace && !archived && (
              <div style={{ marginTop: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                {confirmArchiveId === workspace.workspaceId ? (
                  <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                    Archive this client workspace?{' '}
                    <button
                      type="button"
                      className="inbox-pill-btn"
                      disabled={archivePending}
                      onClick={() => handleArchive(workspace.workspaceId)}
                    >
                      Confirm
                    </button>{' '}
                    <button
                      type="button"
                      className="inbox-pill-btn"
                      onClick={() => setConfirmArchiveId(null)}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="inbox-pill-btn"
                    onClick={() => setConfirmArchiveId(workspace.workspaceId)}
                  >
                    Archive
                  </button>
                )}
              </div>
            )}
            {workspace.isOwner && archived && (
              <div style={{ marginTop: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  disabled={archivePending}
                  onClick={() => handleRestore(workspace.workspaceId)}
                >
                  Restore
                </button>
              </div>
            )}
          </div>
          {!workspace.isSelected && !archived && (
            <button
              type="button"
              className="inbox-pill-btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSwitchWorkspace(workspace.workspaceId);
              }}
              style={{ flexShrink: 0 }}
            >
              Switch
            </button>
          )}
        </div>
      </div>
    );
  }
}
